/*eslint-disable*/
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// @mui/material components
import { makeStyles } from '@mui/styles';
// material ui icons
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ParametersIcon from '@mui/icons-material/FormatListBulleted';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import ReactTable from 'components/ReactTable/ReactTable.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import DatePicker from 'components/CustomInput/CalendarInput.js';
import ParameterEditor from './ParameterEditor';
import ParameterSelector from './ParameterSelector';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/parameterSetEditStyle.js';

import { log } from '@src/Log.init';
import {
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
} from '@themes/defaultThemeProps';
// Redux
import { Box, Typography } from '@mui/material';
import Icon from '@mui/material/Icon';
import {
  useAddParameterSetMutation,
  useGetClientParameterDictionaryQuery,
  useUpdateParametersMutation,
} from 'api/parameter.api';
import { useGetTenantQuery } from 'api/tenant.api';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import { toast } from 'react-toastify';

const useStyles = makeStyles(styles);
let pendingRedirectUrl = null;

const getEmptyParameterSet = () => {
  return {
    name: '',
    description: '',
    temporary: false,
    start_date: '',
    end_date: '',
    parameters: [],
  };
};

export default function ParameterSet(props) {
  const classes = useStyles();

  const dispatch = useDispatch();
  const location = useLocation();

  const viewIsDirty = useSelector(selectViewIsDirty);
  const { data: parameterDictionary } = useGetClientParameterDictionaryQuery();
  const { data: tenant } = useGetTenantQuery();
  const [updateParameterSet] = useUpdateParametersMutation();
  const [createParameterSet] = useAddParameterSetMutation();
  
  const isMigrationComplete = tenant?.supported_languages !== undefined;

  const newParameterSet = location.state.new;
  const parameterSet = newParameterSet
    ? getEmptyParameterSet()
    : location.state.parameterSet;
  const parameters = parameterSet.parameters;

  const [version, setVersion] = useState(parameterSet.version);
  const [name, setName] = useState(parameterSet.name);
  const [nameState, setNameState] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState(parameterSet.description);
  const [descriptionState, setDescriptionState] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [temporary, setTemporary] = useState(parameterSet.temporary);
  const [temporaryState, setTemporaryState] = useState('');
  const [startDate, setStartDate] = useState(
    parameterSet.start_date ? parameterSet.start_date : ''
  );
  const [startDateState, setStartDateState] = useState('');
  const [endDate, setEndDate] = useState(parameterSet.end_date);
  const [endDateState, setEndDateState] = useState('');

  const [errorTemplatesCount, setErrorTemplatesCount] = useState('');
  const [hasAddedItem, setHasAddedItem] = useState(false);

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    }
    return false;
  };

  const isEndDateValid = function (current) {
    const start = moment(startDate);
    if (startDate) return current.isSameOrAfter(start);
    else return true;
  };
  const isStartDateValid = function (current) {
    const end = moment(endDate);
    if (endDate) return current.isSameOrBefore(end);
    else return true;
  };

  const getDateFromPickerEvent = (event) => {
    if (event?._isAMomentObject && typeof event.toDate === 'function') {
      const date = event.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof event === 'string') {
      if (event.trim() === '') {
        return '';
      }

      return null;
    }

    if (event instanceof Date) {
      return Number.isNaN(event.getTime()) ? null : event;
    }

    return null;
  };

  const [editParameter, setEditParameter] = React.useState(false);
  const [currentParameterIndex, setCurrentParameterIndex] = React.useState();

  const [confirmDelete, setConfirmDelete] = React.useState(false);
  
  const handleEditParameter = (index) => {
    setCurrentParameterIndex(index);
    setEditParameter(true);
  };

  const handleDeleteParameter = (index) => {
    setCurrentParameterIndex(index);
    setConfirmDelete(true);
  };

  const handleCloseParameter = () => {
    setEditParameter(false);
  };

  const handleParameterUpdate = (updatedParameter) => {
    var newData = [...data]; // copying the old data array

    newData[currentParameterIndex].value =
      newData[currentParameterIndex].type === 'Selection' &&
      !updatedParameter.value
        ? 'None'
        : updatedParameter.value;
    newData[currentParameterIndex].immutable =
      updatedParameter.immutable === true ? 'True' : 'False';
    newData[currentParameterIndex].changed = 'Yes';
    setData(newData); // copy new array over old
    dispatch(setViewIsDirty(true));
  };

  const computeInitialData = (params, dictionary, tenantData, set) => {
    if (!dictionary) return [];
    const isMigrationComplete = tenantData?.supported_languages !== undefined;
    return params
      .filter((parameter) => {
        if (parameter.name === 'Supported_Languages' && isMigrationComplete) {
          return false;
        }
        const entry = dictionary.find(
          (e) => e.name === parameter.name
        );
        if (!entry) {
          log.error({
            message_type: 'SystemError',
            message: `Parameter ${parameter.name} from set ${set.name} not found in dictionary.`,
          });
          return false;
        }
        return true;
      })
      .reduce((acc, parameter, key) => {
        const entry = dictionary.find(
          (e) => e.name === parameter.name
        );
        const formattedValue =
          entry.type === 'Selection' && !parameter.value
            ? 'None'
            : parameter.value;
        acc.push({
          id: key,
          category: entry.category,
          parameter: { name: entry.name, description: entry.description },
          value: formattedValue,
          type: entry.type,
          immutable: parameter.immutable === true ? 'True' : 'False',
          actions: (
            <div className='actions-left'>
              <Button
                justIcon
                round
                simple
                onClick={() => handleEditParameter(key)}
                color='warning'
                className='edit'
              >
                <Edit />
              </Button>
              <Button
                justIcon
                round
                simple
                onClick={() => handleDeleteParameter(key)}
                color='warning'
                className='edit'
              >
                <Delete />
              </Button>
            </div>
          ),
        });
        return acc;
      }, [])
      .filter((entry) => entry !== null);
  };

  const [data, setData] = React.useState([]);

  useEffect(() => {
    if (!parameterDictionary) return;
    setData(
      computeInitialData(
        parameters,
        parameterDictionary,
        tenant,
        parameterSet
      )
    );
  }, [parameterSet.name, !!parameterDictionary, !!tenant]);

  const formatParameterCell = ({ value }) => {
    return (
      <div>
        <span className={classes.parameterName}>{value.name}</span>
        <span className={classes.parameterDescription}>
          {value.description}
        </span>
      </div>
    );
  };

  const [selectParameters, setSelectParameters] = React.useState(false);
  const addParameterHandler = () => {
    setSelectParameters(true);
  };

  const handleCloseParameterSelector = () => {
    setSelectParameters(false);
  };

  const deleteParameter = () => {
    data.splice(currentParameterIndex, 1); // remove entry
    // renumber data entries
    const newData = data.map((parameter, key) => {
      const parameterRow = {
        id: key,
        category: parameter.category,
        parameter: parameter.parameter,
        value: parameter.value,
        type: parameter.type,
        immutable: parameter.immutable,
        actions: (
          <div className='actions-left'>
            <Button
              justIcon
              round
              simple
              onClick={() => handleEditParameter(key)}
              color='warning'
              className='edit'
            >
              <Edit />
            </Button>
            <Button
              justIcon
              round
              simple
              onClick={() => handleDeleteParameter(key)}
              color='warning'
              className='edit'
            >
              <Delete />
            </Button>
          </div>
        ),
      };
      return parameterRow;
    });
    dispatch(setViewIsDirty(true));
    setData(newData);
    setConfirmDelete(false);
  };

  const handleAddParameters = async (parameters) => {
    let newData = [...data]; // copying the old data array
    let offset = newData.length;
    for (var i = 0; i < parameters.length; i++) {
      const parameter = parameters[i];
      const newParameter = {
        id: parameter.key + offset,
        category: parameter.category,
        parameter: { name: parameter.name, description: parameter.description },
        value: parameter.default,
        type: parameter.type,
        immutable: 'False',
        changed: 'No',
        actions: (
          <div className='actions-left'>
            <Button
              justIcon
              round
              simple
              onClick={() => handleEditParameter(parameter.key + offset)}
              color='warning'
              className='edit'
            >
              <Edit />
            </Button>
            <Button
              justIcon
              round
              simple
              onClick={() => handleDeleteParameter(parameter.key + offset)}
              color='warning'
              className='edit'
            >
              <Delete />
            </Button>
          </div>
        ),
      };
      newData.push(newParameter);
    }
    dispatch(setViewIsDirty(true));
    setData(newData); // copy new array over old
    setHasAddedItem(true);
  };

  const editableParameter = () => {
    const dataEntry = data[currentParameterIndex];
    const dictionaryEntry = parameterDictionary.find(
      (entry) => entry.name === dataEntry.parameter.name
    );
    return {
      ...dictionaryEntry,
      value: dataEntry.value,
      immutable: dataEntry.immutable,
    };
  };

  const changeClick = async () => {
    if (viewIsDirty && data?.length === 0) {
      setErrorTemplatesCount(
        'You must add at least one parameter to save this parameter set'
      );
      return;
    }
    let error = false;
    const updatedSet = cloneDeep(parameterSet);

    if (!verifyLength(name, name, 3)) {
      error = true;
      setNameState('error');
    }
    if (!verifyLength(description, description, 3)) {
      error = true;
      setDescriptionState('error');
    }
    if (error === false) {
      const isTemporary = !!(endDate && endDate !== '');

      updatedSet.name = name;
      updatedSet.description = description;
      updatedSet.temporary = isTemporary;
      updatedSet.version = version;
      updatedSet.start_date =
        startDate && startDate !== '' ? startDate : null;
      updatedSet.end_date = endDate && endDate !== '' ? endDate : null;
      updatedSet.parameters = data.map((entry) => ({
        name: entry.parameter.name,
        type: entry.type,
        value: entry.value,
        immutable:
          entry.immutable === true ||
          entry.immutable === 'true' ||
          entry.immutable === 'True',
      }));

      try {
        if (newParameterSet) {
          await createParameterSet(updatedSet).unwrap();
        } else {
          await updateParameterSet(updatedSet).unwrap();
        }
        pendingRedirectUrl = '/admin/parameterlists';
        dispatch(setViewIsDirty(false));
      } catch (err) {
        const errors = err?.data?.errors;
        const errorMessage =
          (Array.isArray(errors) && errors.length ? Object.values(errors[0])[0] : null) ||
          err?.data?.message ||
          'An error occurred while trying to save the parameter set';

        toast.error(errorMessage, {
          position: toast.POSITION.BOTTOM_CENTER,
        });
      }
    }
  };

  useEffect(() => {
    dispatch(setViewIsDirty(false));
  }, []);

  useEffect(() => {
    if (pendingRedirectUrl) {
      props.history.push(pendingRedirectUrl);
      pendingRedirectUrl = null;
    }
  }, [viewIsDirty]);

  useEffect(() => {
    if (data.length === 0 && viewIsDirty && hasAddedItem) {
      setErrorTemplatesCount(
        'You must add at least one parameter to save this parameter set'
      );
    } else {
      setErrorTemplatesCount('');
    }
  }, [data, viewIsDirty, hasAddedItem]);

  return (
    <>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color='primary' icon>
              <CardIcon color='primary'>
                <ParametersIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Parameter Set</h4>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={12}>
                  <CustomInput
                    helperText={
                      nameState === 'error'
                        ? nameError ?? 'An error has occurred'
                        : false
                    }
                    success={nameState === 'success'}
                    error={nameState === 'error'}
                    labelText='Parameter Set Name'
                    id='name'
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      defaultValue: name,
                      onChange: (event) => {
                        if (verifyLength(event.target.value, name, 3)) {
                          setNameState('success');
                          setNameError('');
                        } else {
                          setNameState('error');
                          setNameError(
                            'Field must contain at least 3 alphanumeric or special characters'
                          );
                        }
                        setName(event.target.value);
                      },
                      autoComplete: 'off',
                    }}
                  />
                  <CustomInput
                    helperText={
                      descriptionState === 'error'
                        ? descriptionError ?? 'An error has occurred'
                        : false
                    }
                    success={descriptionState === 'success'}
                    error={descriptionState === 'error'}
                    labelText='Parameter Set Description'
                    id='description'
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      defaultValue: description,
                      onChange: (event) => {
                        if (verifyLength(event.target.value, description, 3)) {
                          setDescriptionState('success');
                          setDescriptionError('');
                        } else {
                          setDescriptionState('error');
                        }
                        setDescription(event.target.value);
                        setDescriptionError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      },
                      autoComplete: 'off',
                    }}
                  />
                  <DatePicker
                    isValidDate={(current) => {
                      return endDate ? current.isBefore(endDate) : true;
                    }}
                    success={startDateState === 'success'}
                    error={startDateState === 'error'}
                    labelText='Start Date'
                    id='startdate'
                    validator={isStartDateValid}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: startDate
                        ? new Date(startDate).toDateString()
                        : '',
                      onChange: (event) => {
                        const nextStartDate = getDateFromPickerEvent(event);
                        if (nextStartDate === null) {
                          return;
                        }

                        dispatch(setViewIsDirty(true));
                        setStartDateState(nextStartDate === '' ? '' : 'success');
                        setStartDate(nextStartDate);
                      },
                    }}
                  />
                  <DatePicker
                    isValidDate={(current) => {
                      return startDate ? current.isAfter(startDate) : true;
                    }}
                    success={endDateState === 'success'}
                    error={endDateState === 'error'}
                    labelText='End Date'
                    id='enddate'
                    formControlProps={{
                      fullWidth: true,
                    }}
                    validator={isEndDateValid}
                    inputProps={{
                      value: endDate ? new Date(endDate).toDateString() : '',
                      onChange: (event) => {
                        const nextEndDate = getDateFromPickerEvent(event);
                        if (nextEndDate === null) {
                          return;
                        }

                        dispatch(setViewIsDirty(true));
                        setEndDateState(nextEndDate === '' ? '' : 'success');
                        setEndDate(nextEndDate);
                        if (nextEndDate !== '') {
                          setTemporary(true);
                          setTemporaryState('success');
                        } else {
                          setTemporary(false);
                          setTemporaryState('');
                        }
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography className={classes.version}>
                      Version {version}
                    </Typography>
                    <Box>
                      <Button
                        color='primary'
                        onClick={changeClick}
                        disabled={!viewIsDirty || data.length === 0}
                        className={classes.registerButton}
                      >
                        Save Changes
                      </Button>
                      <Button
                        color='secondary'
                        onClick={() =>
                          props.history.push('/admin/parameterlists')
                        }
                        className={classes.registerButton}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color='primary' icon>
              <CardIcon color='primary'>
                <ParametersIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Parameter Values</h4>
              {errorTemplatesCount && (
                <div className={classes.errListTemplatesMsg}>
                  <Icon>info</Icon> {errorTemplatesCount}
                </div>
              )}
              <Button
                color='primary'
                onClick={addParameterHandler}
                className={classes.registerButton}
              >
                + Add Parameter
              </Button>
            </CardHeader>
            <CardBody>
              <ReactTable
                columns={[
                  {
                    Header: 'Category',
                    accessor: 'category',
                    disableSortBy: true,
                    width: 100,
                  },
                  {
                    Header: 'Parameter',
                    accessor: 'parameter',
                    Cell: formatParameterCell,
                    filter: 'parameter',
                    disableSortBy: true,
                    width: 200,
                  },
                  {
                    Header: 'Value',
                    accessor: 'value',
                    disableFilters: true,
                    disableSortBy: true,
                    width: 150,
                    style: {
                      marginBottom: 20,
                    },
                  },
                  {
                    Header: 'Immutable?',
                    accessor: 'immutable',
                    disableFilters: true,
                    disableSortBy: true,
                    width: 50,
                    style: {
                      marginBottom: 40,
                      width: '10%',
                    },
                  },
                  {
                    Header: 'Actions',
                    accessor: 'actions',
                    disableFilters: true,
                    disableSortBy: true,
                    width: 50,
                    style: {
                      marginTop: 0,
                      width: '10%',
                    },
                  },
                ]}
                data={[...data].sort((a, b) => {
                  const categoryCompare = a.category.localeCompare(b.category);
                  return categoryCompare === 0
                    ? a.parameter.name.localeCompare(b.parameter.name)
                    : categoryCompare;
                })}
                options={{
                  pageSize: (parameterDictionary?.length ?? 0) + 1,
                }}
              />
            </CardBody>
          </Card>
        </GridItem>
        {editParameter === true ? (
          <ParameterEditor
            open={editParameter}
            parameter={editableParameter()}
            handleClose={handleCloseParameter}
            handleUpdate={handleParameterUpdate}
          />
        ) : null}
        {selectParameters === true ? (
          <ParameterSelector
            open={selectParameters}
            handleClose={handleCloseParameterSelector}
            handleAdd={handleAddParameters}
            dictionary={parameterDictionary.filter(
              (p) => !data.find((d) => d.parameter.name === p.name) && 
              !(p.name === 'Supported_Languages' && isMigrationComplete)
            )}
          />
        ) : null}
      </GridContainer>
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          Delete{' '}
          {data[currentParameterIndex]
            ? data[currentParameterIndex].parameter.name
            : ''}
          ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to remove this parameter?
            <br />
            Press Delete to confirm, or Cancel to cancel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn onClick={() => setConfirmDelete(false)}>
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn
            onClick={() => deleteParameter()}
            color='primary'
            autoFocus
          >
            Delete
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}
